import ExcelJS from 'exceljs'
import { savingsRange } from './networkUtils'

const PRIMARY = '1E5045'
const HEADER_BG = '0E4749'
const WHITE = 'FFFFFF'
const LIGHT_GRAY = 'F8FAFB'
const GREEN = '059669'

const TYPE_LABEL = {
  negotiation: 'Negotiation',
  sole_source: 'Sole-Source Risk',
  part_family: 'Part Family',
  quick_win: 'Quick Win',
  site_gap: 'Site Price Gap',
}

/**
 * Export all opportunities (summary + per-opportunity line items) to XLSX.
 * Mirrors the demo's Excel export, with a dedicated per-site sheet for the
 * cross-site price-gap findings.
 */
export async function exportOpportunitiesXlsx(opps) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SourceOptima'
  wb.created = new Date()

  // ── Sheet 1: Opportunities summary ──
  const ws = wb.addWorksheet('Opportunities')
  ws.columns = [
    { header: 'Opportunity', key: 'title', width: 52 },
    { header: 'Type', key: 'type', width: 18 },
    { header: 'Parts', key: 'parts', width: 8 },
    { header: 'Spend Exposed (€)', key: 'exposed', width: 18 },
    { header: 'Savings Low (€)', key: 'lo', width: 16 },
    { header: 'Savings High (€)', key: 'hi', width: 16 },
    { header: 'Confidence', key: 'conf', width: 12 },
  ]
  styleHeader(ws.getRow(1))

  opps.forEach(o => {
    const r = savingsRange(o.estSavings)
    const row = ws.addRow({
      title: o.title,
      type: TYPE_LABEL[o.type] || o.type,
      parts: o.parts.length,
      exposed: Math.round(o.spendExposed),
      lo: Math.round(r.lo),
      hi: Math.round(r.hi),
      conf: o.confidence,
    })
    ;['exposed', 'lo', 'hi'].forEach(k => { row.getCell(k).numFmt = '#,##0' })
    row.getCell('lo').font = { color: { argb: GREEN }, bold: true }
    row.getCell('hi').font = { color: { argb: GREEN }, bold: true }
  })
  zebra(ws, 2)

  // ── Sheet 2: Line items (parts × suppliers) ──
  const li = wb.addWorksheet('Line Items')
  li.columns = [
    { header: 'Opportunity', key: 'opp', width: 40 },
    { header: 'Type', key: 'type', width: 16 },
    { header: 'Part #', key: 'pn', width: 18 },
    { header: 'Description', key: 'desc', width: 34 },
    { header: 'Supplier', key: 'sup', width: 26 },
    { header: 'Unit Cost (€)', key: 'uc', width: 14 },
    { header: 'Price Pos.', key: 'pp', width: 11 },
    { header: 'Spend (€)', key: 'spend', width: 14 },
  ]
  styleHeader(li.getRow(1))
  opps.forEach(o => {
    o.parts.forEach(p => {
      (p.suppliers || []).forEach(s => {
        const row = li.addRow({
          opp: o.title.slice(0, 60), type: TYPE_LABEL[o.type] || o.type,
          pn: p.pn, desc: p.description || '', sup: s.name,
          uc: s.unit_cost ? Number(s.unit_cost) : null,
          pp: s.price_position != null ? s.price_position : null,
          spend: Math.round(s.spend || 0),
        })
        row.getCell('uc').numFmt = '#,##0.00'
        row.getCell('spend').numFmt = '#,##0'
        if (s.price_position != null) {
          row.getCell('pp').numFmt = '0.00"x"'
          if (s.price_position > 1.5) row.getCell('pp').font = { color: { argb: 'DC2626' }, bold: true }
        }
      })
    })
  })
  zebra(li, 2)

  // ── Sheet 3: Site price gaps (per-site detail) ──
  const siteOpps = opps.filter(o => o.type === 'site_gap')
  if (siteOpps.length) {
    const sg = wb.addWorksheet('Site Price Gaps')
    sg.columns = [
      { header: 'Part #', key: 'pn', width: 18 },
      { header: 'Description', key: 'desc', width: 28 },
      { header: 'Site', key: 'site', width: 22 },
      { header: 'Supplier(s)', key: 'sup', width: 30 },
      { header: 'Unit Cost (€)', key: 'uc', width: 14 },
      { header: 'Δ vs Best', key: 'delta', width: 12 },
      { header: 'Site Spend (€)', key: 'spend', width: 16 },
    ]
    styleHeader(sg.getRow(1))
    siteOpps.forEach(o => {
      const best = o.vizData.best
      o.vizData.sites.forEach(s => {
        const over = best > 0 ? s.unit_cost / best - 1 : 0
        const row = sg.addRow({
          pn: o.vizData.pn, desc: o.vizData.desc || '', site: s.site, sup: (s.suppliers || []).join(', '),
          uc: Number(s.unit_cost), delta: over, spend: Math.round(s.spend),
        })
        row.getCell('uc').numFmt = '#,##0.00'
        row.getCell('spend').numFmt = '#,##0'
        row.getCell('delta').numFmt = '+0%;-0%'
        if (over <= 0) row.getCell('delta').font = { color: { argb: GREEN }, bold: true }
        else if (over > 0.3) row.getCell('delta').font = { color: { argb: 'DC2626' }, bold: true }
      })
    })
    zebra(sg, 2)
  }

  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `SourceOptima_Opportunities_${new Date().toISOString().slice(0, 10)}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function styleHeader(row) {
  row.eachCell(c => {
    c.font = { bold: true, color: { argb: WHITE }, size: 11 }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    c.alignment = { vertical: 'middle' }
    c.border = { bottom: { style: 'thin', color: { argb: PRIMARY } } }
  })
  row.height = 20
}

function zebra(ws, from) {
  for (let i = from; i <= ws.rowCount; i++) {
    if (i % 2 === 0) {
      ws.getRow(i).eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } }
      })
    }
  }
}
